import {
	Box,
	Breadcrumbs,
	CircularProgress,
	Link,
	Paper,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import {
	Link as RouterLink,
	matchPath,
	NavLink,
	Outlet,
	useLocation,
	useParams,
} from 'react-router-dom';
import {
	ROLES_AND_PERMISSION_ENABLED,
	TABLES_AND_STATIC_DOCS_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import ConfigProvider, { useConfigContext } from '../../../context/config';
import { withProviders } from '../../../hoc/withProviders';
import SsoConfigApi from './api/config';
import TenantsAPI from './api/tenants';

const navHeaderHeight = 64;
const containerPadding = 48;
const breadcrumbHeight = 22;
const spacing = 16;

interface TabPanelProps {
	children: React.ReactNode;
}

function useRouteMatch(patterns: readonly string[]) {
	const { pathname } = useLocation();

	for (let i = 0; i < patterns.length; i += 1) {
		const pattern = patterns[i];
		const possibleMatch = matchPath({ path: pattern }, pathname);
		if (possibleMatch !== null) {
			return possibleMatch;
		}
	}

	return null;
}

function TabPanel(props: TabPanelProps) {
	const { children } = props;

	return (
		<Box
			role='tabpanel'
			id={`tenant-tabpanel`}
			aria-labelledby={`tenant-tab`}
			sx={{
				height: `calc(100vh - ${navHeaderHeight}px - ${containerPadding}px - ${breadcrumbHeight}px - ${spacing}px)`,
				mt: 0,
				flexGrow: 1,
				p: 3,
				backgroundColor: 'white.light',
			}}
		>
			{children}
		</Box>
	);
}

const NamespaceTenantManagement = () => {
	const params = useParams();
	const authData = useAuthContext();
	const { tenantHostname } = params;
	const { setProductConfigs, productConfigs } = useConfigContext();
	const { data: products } = useQuery({
		queryKey: ['product-config', tenantHostname],
		queryFn: () => SsoConfigApi.getProductConfigs(tenantHostname),
	});
	const { isLoading: isTenantsLoading, data: tenants } = useQuery({
		queryKey: ['tenants', authData?.adminAuthData?.accountId],
		queryFn: TenantsAPI.getTenants,
	});

	const tenant = tenants
		? tenants.find((tenant) => tenant.hostname === tenantHostname)
		: null;

	const { isLoading: isTenantAuthLoading, data: tenantAuthorizationData } =
		useQuery({
			queryKey: ['tenant-auth', tenantHostname, tenant?.locator],
			queryFn: () => TenantsAPI.authorizeTenant({ tenantId: tenant?.locator }),
			enabled: !!tenant,
			onSettled: (data) => {
				authData.setTenantAuth(data);
			},
		});

	const routeMatch = useRouteMatch([
		'/admin/tenants/:tenantHostname/integrations/:integrationName',
		'/admin/tenants/:tenantHostname/roles/edit/:roleName',
		'/admin/tenants/:tenantHostname/roles/new',
		'/admin/tenants/:tenantHostname/roles',
		'/admin/tenants/:tenantHostname/users',
		'/admin/tenants/:tenantHostname/tables',
		'/admin/tenants/:tenantHostname/static-docs',
		'/admin/tenants/:tenantHostname/resource-groups',
		'/admin/tenants/:tenantHostname/script-logs',
	]);
	let currentTab = routeMatch?.pattern?.path;

	if (
		currentTab === '/admin/tenants/:tenantHostname/roles/edit/:roleName' ||
		currentTab === '/admin/tenants/:tenantHostname/roles/new' ||
		currentTab === '/admin/tenants/:tenantHostname/roles'
	) {
		currentTab = '/admin/tenants/:tenantHostname/roles';
	}

	if (products && !productConfigs) {
		setProductConfigs(products);
	}

	const tablesAndStaticDocsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			TABLES_AND_STATIC_DOCS_ENABLED
		];

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			{isTenantAuthLoading || isTenantsLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center' }}>
					<CircularProgress />
				</Box>
			) : (
				<Stack spacing={2}>
					<Breadcrumbs aria-label='breadcrumb'>
						<Link
							component={RouterLink}
							underline='hover'
							color='inherit'
							to='/admin/tenants'
						>
							{'Tenants'}
						</Link>
						<Typography color='text.primary'>{tenantHostname}</Typography>
					</Breadcrumbs>

					<Paper
						elevation={2}
						sx={{
							display: 'flex',
							height: '100%',
							flexDirection: 'column',
						}}
					>
						<Stack direction='row'>
							<Stack direction='column'>
								<Tabs
									orientation='vertical'
									value={currentTab}
									aria-label='tenant navigation tabs'
								>
									{tenantAuthorizationData?.configProperties?.[
										ROLES_AND_PERMISSION_ENABLED
									] && (
										<Tab
											label='Users'
											value='/admin/tenants/:tenantHostname/users'
											component={NavLink}
											to='users'
										/>
									)}
									{tenantAuthorizationData?.configProperties?.[
										ROLES_AND_PERMISSION_ENABLED
									] && (
										<Tab
											label='Roles'
											value='/admin/tenants/:tenantHostname/roles'
											component={NavLink}
											to='roles'
										/>
									)}
									<Tab
										label='Integrations'
										value={
											'/admin/tenants/:tenantHostname/integrations/:integrationName'
										}
										component={NavLink}
										to='integrations/external-raters'
									/>
									<Tab
										label='Script Logs'
										value='/admin/tenants/:tenantHostname/script-logs'
										to='script-logs'
										component={NavLink}
									/>

									{tablesAndStaticDocsEnabled && (
										<>
											<Tab
												label='Tables'
												value='/admin/tenants/:tenantHostname/tables'
												component={NavLink}
												to='tables'
											/>
											<Tab
												label='Static Docs'
												value='/admin/tenants/:tenantHostname/static-docs'
												component={NavLink}
												to='static-docs'
											/>
										</>
									)}
									<Tab
										label='Resource groups'
										value='/admin/tenants/:tenantHostname/resource-groups'
										component={NavLink}
										to='resource-groups'
									/>
								</Tabs>
							</Stack>
							<TabPanel>
								<Outlet />
							</TabPanel>
						</Stack>
					</Paper>
				</Stack>
			)}
		</LocalizationProvider>
	);
};

const NamespaceTenantManagementWithProvider = () => {
	return withProviders([ConfigProvider], <NamespaceTenantManagement />);
};

export default NamespaceTenantManagementWithProvider;
